import React, { useEffect } from 'react';
import { Task, FullProject } from '../../../types';
import { db } from '../../../firebase';
import { collection, doc, query, where, onSnapshot, Query, DocumentData, QuerySnapshot, QueryDocumentSnapshot } from 'firebase/firestore';
import { getCleanTaskData } from '../../../utils/taskComparison';
import { isCommProject } from '../../../utils/idNormalizer';
import {
  shouldAcceptProjectSnapshot,
  getCollaborativeProjectIds,
  buildMergedCloudProjects,
  reconcileLocalAndCloudProjects,
} from './projectMergeEngine';
import { setupInboxListeners } from './projectInboxListeners';

declare global {
  interface Window {
    __isGWanttDragging?: boolean;
    __isGWanttPointerDown?: boolean;
    __pendingMerge?: boolean;
    __flushPendingMerge?: () => void;
  }
}

export interface UseProjectRealtimeListenersParams {
  userId?: string;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  userRole?: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setIsInitialLoadComplete: (v: boolean) => void;
  migrateLegacyProjectTasks: (project: FullProject) => Promise<void>;
  rawProjectsRef: React.MutableRefObject<FullProject[]>;
  rawTasksRef: React.MutableRefObject<Task[]>;
  mergeProjectsAndTasksRef: React.MutableRefObject<(() => void) | null>;
  ownedTasksRef: React.MutableRefObject<Task[]>;
  sharedTasksMapRef: React.MutableRefObject<Map<string, Task>>;
  lastSharedIdsKeyRef: React.MutableRefObject<string>;
  syncedTasksRef: React.MutableRefObject<Map<string, string>>;
  tasksLoadedRef: React.MutableRefObject<boolean>;
  migratedLegacyProjectIdsRef: React.MutableRefObject<Set<string>>;
  dirtyProjectIdsRef: React.MutableRefObject<Set<string>>;
  dirtyTaskIdsRef: React.MutableRefObject<Set<string>>;
  taskBaselinesRef: React.MutableRefObject<Map<string, Task>>;
  recentlyDeletedTaskIdsRef: React.MutableRefObject<Set<string>>;
  syncingProjectIdsRef: React.MutableRefObject<Set<string>>;
  syncedProjectTimestampsRef: React.MutableRefObject<Map<string, number>>;
}

export function useProjectRealtimeListeners({
  userId,
  user,
  tenantId,
  userRole,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  normalizeProjectId,
  setAllProjects,
  setIsInitialLoadComplete,
  migrateLegacyProjectTasks,
  rawProjectsRef,
  rawTasksRef,
  mergeProjectsAndTasksRef,
  ownedTasksRef,
  sharedTasksMapRef,
  lastSharedIdsKeyRef,
  syncedTasksRef,
  tasksLoadedRef,
  migratedLegacyProjectIdsRef,
  dirtyProjectIdsRef,
  dirtyTaskIdsRef,
  taskBaselinesRef,
  recentlyDeletedTaskIdsRef,
  syncingProjectIdsRef,
  syncedProjectTimestampsRef,
}: UseProjectRealtimeListenersParams) {
  useEffect(() => {
    if (!userId || userId === 'e2e_local_uid') {
      tasksLoadedRef.current = true;
      setIsInitialLoadComplete(true);
      return;
    }

    const mergeProjectsAndTasks = () => {
      if (window.__isGWanttDragging || window.__isGWanttPointerDown) {
        window.__pendingMerge = true;
        return;
      }
      const mergedProjects = buildMergedCloudProjects(rawProjectsRef.current, rawTasksRef.current, {
        tasksLoaded: tasksLoadedRef.current,
        migratedLegacyProjectIds: migratedLegacyProjectIdsRef.current,
        recentlyDeletedTaskIds: recentlyDeletedTaskIdsRef.current,
        COMM_PROJECT_ID,
        normalizeProjectId,
        onMigrateLegacyProject: migrateLegacyProjectTasks,
      });

      setAllProjects((prev) =>
        reconcileLocalAndCloudProjects(prev, mergedProjects, {
          dirtyProjectIds: dirtyProjectIdsRef.current,
          syncingProjectIds: syncingProjectIdsRef.current,
          dirtyTaskIds: dirtyTaskIdsRef.current,
          taskBaselines: taskBaselinesRef.current,
          recentlyDeletedTaskIds: recentlyDeletedTaskIdsRef.current,
          syncedProjectTimestamps: syncedProjectTimestampsRef.current,
          COMM_PROJECT_ID,
          INBOX_PROJECT_ID,
          normalizeProjectId,
        })
      );
    };

    mergeProjectsAndTasksRef.current = () => mergeProjectsAndTasks();
    if (typeof window !== 'undefined') {
      window.__flushPendingMerge = () => mergeProjectsAndTasks();
    }

    const rebuildAllTasks = () => {
      const deletedIds = recentlyDeletedTaskIdsRef.current;
      const owned = ownedTasksRef.current.filter((t) => !deletedIds.has(String(t.id)));
      const shared = Array.from(sharedTasksMapRef.current.values()).filter(
        (t) => !deletedIds.has(String(t.id))
      );

      const uniqueTasksMap = new Map<string, Task>();
      owned.forEach((t) => uniqueTasksMap.set(t.id.toString(), t));
      shared.forEach((t) => uniqueTasksMap.set(t.id.toString(), t));
      rawTasksRef.current = Array.from(uniqueTasksMap.values());

      const cache = syncedTasksRef.current;
      rawTasksRef.current.forEach((t) => {
        if (!dirtyTaskIdsRef.current.has(t.id.toString())) {
          cache.set(t.id.toString(), JSON.stringify(getCleanTaskData(t)));
          taskBaselinesRef.current.set(t.id.toString(), t);
        }
      });

      if (mergeProjectsAndTasksRef.current) mergeProjectsAndTasksRef.current();
    };

    let ownedProjects: FullProject[] = [];
    const sharedProjects: FullProject[] = [];
    let unsubSharedTasksList: (() => void)[] = [];
    const userEmail = user?.email?.toLowerCase() || '';

    const handleProjectsUpdate = (owned: FullProject[], shared: FullProject[]) => {
      const allFetched = [...owned, ...shared];
      const currentProjects = rawProjectsRef.current;
      const updatedProjects = [...currentProjects];

      allFetched.forEach((fp) => {
        const idx = updatedProjects.findIndex(
          (p) =>
            p.id === fp.id ||
            (p.rawId && p.rawId === fp.id) ||
            (normalizeProjectId(p.id) === normalizeProjectId(fp.id) &&
              (isCommProject(fp.id) || fp.id.startsWith('inbox_inforum')))
        );
        if (idx === -1) {
          updatedProjects.push(fp);
        } else {
          updatedProjects[idx] = fp;
        }
      });

      const fetchedIds = new Set(allFetched.map((p) => p.id));
      const finalProjects = updatedProjects.filter(
        (p) =>
          p.id === INBOX_PROJECT_ID ||
          p.id === 'inbox_inforum' ||
          p.id === COMM_PROJECT_ID ||
          p.id === 'comm_internal' ||
          p.id.startsWith('local_') ||
          fetchedIds.has(p.id)
      );

      rawProjectsRef.current = finalProjects;
      if (mergeProjectsAndTasksRef.current) mergeProjectsAndTasksRef.current();

      const sharedProjIds = getCollaborativeProjectIds(allFetched, userId, INBOX_PROJECT_ID);

      const sharedIdsKey = sharedProjIds.sort().join(',');
      if (sharedIdsKey !== lastSharedIdsKeyRef.current) {
        lastSharedIdsKeyRef.current = sharedIdsKey;
        unsubSharedTasksList.forEach((u) => u());
        unsubSharedTasksList = [];

        if (sharedProjIds.length > 0) {
          const chunks: string[][] = [];
          for (let i = 0; i < sharedProjIds.length; i += 30) {
            chunks.push(sharedProjIds.slice(i, i + 30));
          }
          chunks.forEach((chunk) => {
            const qSharedTasks = query(collection(db, 'tasks'), where('projectId', 'in', chunk));
            const unsub = onSnapshot(
              qSharedTasks,
              { includeMetadataChanges: false },
              (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                  if (change.type === 'removed') {
                    sharedTasksMapRef.current.delete(change.doc.id);
                  } else {
                    const data = change.doc.data();
                    sharedTasksMapRef.current.set(change.doc.id, {
                      ...data,
                      id: change.doc.id,
                      start: data.start || '',
                      end: data.end || '',
                      completed: !!data.completed,
                      status: data.status || 'todo',
                    } as Task);
                  }
                });
                rebuildAllTasks();
              },
              (err) => {
                console.warn('[ProjectManager] Shared tasks listener error (non-fatal):', err);
                sharedTasksMapRef.current.clear();
                rebuildAllTasks();
              }
            );
            unsubSharedTasksList.push(unsub);
          });
        } else {
          sharedTasksMapRef.current.clear();
          rebuildAllTasks();
        }
      }
    };

    const isAdmin = false;
    const unsubscribers: (() => void)[] = [];
    const projectSnapshots = new Map<string, Map<string, FullProject>>();

    const registerProjectsListener = (queryKey: string, q: Query<DocumentData>) => {
      const unsub = onSnapshot(
        q,
        { includeMetadataChanges: false },
        (snapshot: QuerySnapshot<DocumentData>) => {
          const queryMap = new Map<string, FullProject>();
          snapshot.docs.forEach((docSnap: QueryDocumentSnapshot<DocumentData>) => {
            const data = docSnap.data();
            const fp = { ...data, id: docSnap.id } as FullProject;
            if (
              !shouldAcceptProjectSnapshot(fp, {
                userId,
                tenantId,
                INBOX_PROJECT_ID,
                COMM_PROJECT_ID,
              })
            ) {
              return;
            }
            queryMap.set(fp.id, fp);
          });
          projectSnapshots.set(queryKey, queryMap);

          const mergedOwned = new Map<string, FullProject>();
          projectSnapshots.forEach((map) => {
            map.forEach((proj, id) => {
              mergedOwned.set(id, proj);
            });
          });
          ownedProjects = Array.from(mergedOwned.values());
          handleProjectsUpdate(ownedProjects, sharedProjects);
        },
        (err) => {
          console.warn(`[ProjectManager] Projects listener error for ${queryKey}:`, err);
        }
      );
      unsubscribers.push(unsub);
    };

    if (isAdmin) {
      const qAll = query(collection(db, 'projects'), where('tenantId', '==', tenantId));
      registerProjectsListener('all', qAll);
    } else {
      const qOwned = query(collection(db, 'projects'), where('userId', '==', userId));
      registerProjectsListener('owned', qOwned);

      const qPublic = query(
        collection(db, 'projects'),
        where('tenantId', '==', tenantId),
        where('visibility', '==', 'public')
      );
      registerProjectsListener('public', qPublic);

      const qSample = query(
        collection(db, 'projects'),
        where('tenantId', '==', tenantId),
        where('isSample', '==', true)
      );
      registerProjectsListener('sample', qSample);
    }

    if (userEmail) {
      const qSharedViewer = query(
        collection(db, 'projects'),
        where('sharedWith', 'array-contains', userEmail)
      );
      registerProjectsListener('sharedViewer', qSharedViewer);

      const qSharedEditor = query(
        collection(db, 'projects'),
        where('sharedEditors', 'array-contains', userEmail)
      );
      registerProjectsListener('sharedEditor', qSharedEditor);
    }

    const inboxUnsubs = setupInboxListeners({
      INBOX_PROJECT_ID,
      tenantId,
      rawTasksRef,
      rawProjectsRef,
      mergeProjectsAndTasksRef,
    });
    unsubscribers.push(...inboxUnsubs);

    const qTasks = isAdmin
      ? query(collection(db, 'tasks'), where('tenantId', '==', tenantId))
      : query(collection(db, 'tasks'), where('userId', '==', userId));
    const unsubTasks = onSnapshot(
      qTasks,
      { includeMetadataChanges: false },
      (snapshot) => {
        const fetchedTasks = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            ...data,
            id: docSnap.id,
            start: data.start || '',
            end: data.end || '',
            completed: !!data.completed,
            status: data.status || 'todo',
          } as Task;
        });

        ownedTasksRef.current = fetchedTasks;
        tasksLoadedRef.current = true;
        setIsInitialLoadComplete(true);
        if (loadCompleteTimeoutRef) clearTimeout(loadCompleteTimeoutRef);

        rebuildAllTasks();
      },
      (err) => {
        console.warn('[ProjectManager] Tasks listener error:', err);
        setIsInitialLoadComplete(true);
        if (loadCompleteTimeoutRef) clearTimeout(loadCompleteTimeoutRef);
      }
    );
    unsubscribers.push(unsubTasks);

    let loadCompleteTimeoutRef: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      console.warn('[ProjectManager] isInitialLoadComplete timeout fallback triggered.');
      tasksLoadedRef.current = true;
      setIsInitialLoadComplete(true);
    }, 5000);

    return () => {
      if (loadCompleteTimeoutRef) clearTimeout(loadCompleteTimeoutRef);
      loadCompleteTimeoutRef = null;
      unsubscribers.forEach((unsub) => unsub());
      unsubSharedTasksList.forEach((u) => u());
      unsubSharedTasksList = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user?.email, tenantId, migrateLegacyProjectTasks]);
}
